import React, { useContext, useEffect, useMemo, useState } from "react";
import { Button, Card, Col, Empty, Row, Skeleton, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { RightOutlined } from "@ant-design/icons";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { FacilityID } from "../../../constants/defaultKeys";
import {
  getAppointmentListApi,
  getIpAdmissionApi,
  getBillsListApi,
  getReceptionDashboardApi,
} from "../../../services/receptionist.services";
import { getReceptionistOutPatientListApi } from "../../../services/receptionistop.services";
import { capitalizeFirstLetter } from "../../../helpers/auth/helper";
import { formatToINR } from "../../../helpers/utility";
import AddPatientModal from "../AddPatientModal";
import AppointsModal from "../Appointments/AppointsModal";
import InPatientModal from "../Inpatients/InPatientModal";
import styles from "./ReceptionistDashboard.module.css";

import cardTodayAppointmentsIcon from "../../../assets/receptionistDashboard/card-todays-appointments.svg";
import cardInpatientsActiveIcon from "../../../assets/receptionistDashboard/card-inpatients-active.svg";
import cardOutpatientIcon from "../../../assets/receptionistDashboard/card-outpatient.svg";
import cardAmountCollectedIcon from "../../../assets/receptionistDashboard/card-amount-collected.svg";
import actionBookAppointmentIcon from "../../../assets/receptionistDashboard/action-book-appointment.svg";
import actionAddInpatientIcon from "../../../assets/receptionistDashboard/action-add-inpatient.svg";
import newPatientIcon from "../../../assets/receptionistDashboard/icon-new-patient.svg";

const dateFormat = "YYYY-MM-DD";

const safeNumber = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);

const formatCompactNumber = (n) => {
  if (typeof n !== "number" || !Number.isFinite(n)) return "--";
  if (Math.abs(n) < 1000) return String(n);
  const out = new Intl.NumberFormat("en-IN", {
    notation: "compact",
    compactDisplay: "short",
    maximumFractionDigits: 1,
  }).format(n);
  return out.replace(/K\b/g, "k");
};

const formatTime = (value) => {
  if (!value) return "-";
  return dayjs(value?.split(":").slice(0, 2).join(":"), "HH:mm").format("hh : mm A");
};

const StatusTag = ({ status }) => {
  const normalized = (status || "").toLowerCase();
  const map = {
    consulted: { color: "green", label: "Consulted" },
    reported: { color: "green", label: "Reported" },
    scheduled: { color: "blue", label: "Scheduled" },
    pending: { color: "blue", label: "Pending" },
    active: { color: "green", label: "Active" },
    cancelled: { color: "orange", label: "Cancelled" },
    canceled: { color: "orange", label: "Cancelled" },
  };
  const cfg = map[normalized] || { color: "default", label: status ? capitalizeFirstLetter(status) : "-" };
  return (
    <Tag color={cfg.color} style={{ borderRadius: 999, paddingInline: 12, minWidth: 90, textAlign: "center", display: "inline-block" }}>
      {cfg.label}
    </Tag>
  );
};

const ReceptionistDashboard = ({ refresh }) => {
  const { userDetails } = useContext(UserContext);
  const facilityId = useMemo(
    () => Number(getCookie(FacilityID)) || userDetails?.currFacility,
    [userDetails?.currFacility]
  );

  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingAppts, setLoadingAppts] = useState(true);

  const [todayAppointmentsCount, setTodayAppointmentsCount] = useState(null);
  const [activeInpatientsCount, setActiveInpatientsCount] = useState(null);
  const [outPatientsCount, setOutPatientsCount] = useState(null);
  const [amountCollected, setAmountCollected] = useState(null);
  const [cardMeta, setCardMeta] = useState({
    appointmentsUpcoming: null,
    bedOccupancyPercent: null,
    outpatientsCreatedToday: null,
    paymentsDeltaAmount: null,
    paymentsDeltaDirection: null,
  });

  const [appointmentsData, setAppointmentsData] = useState([]);

  const [refreshKey, setRefreshKey] = useState(0);
  const reloadDashboard = () => setRefreshKey((k) => k + 1);

  const [openAddPatient, setOpenAddPatient] = useState(false);
  const [openBookAppointment, setOpenBookAppointment] = useState(false);
  const [openAddInpatient, setOpenAddInpatient] = useState(false);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.matchMedia("(max-width: 991px)").matches
  );

  useEffect(() => {
    const mq = window.matchMedia("(max-width: 991px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);

  useEffect(() => {
    let ignore = false;

    async function loadStats() {
      setLoadingStats(true);
      try {
        const res = await getReceptionDashboardApi(facilityId);
        if (ignore) return;
        const dash = res?.data?.data;

        setTodayAppointmentsCount(safeNumber(dash?.appointments?.total_today));
        setActiveInpatientsCount(safeNumber(dash?.inpatients?.active_count));
        setOutPatientsCount(safeNumber(dash?.outpatients?.active_count));

        const payToday = dash?.payments?.total_today;
        setAmountCollected(typeof payToday === "number" ? payToday : null);

        setCardMeta({
          appointmentsUpcoming: safeNumber(dash?.appointments?.not_reported_today),
          bedOccupancyPercent:
            typeof dash?.inpatients?.bed_occupancy_percent === "number"
              ? dash.inpatients.bed_occupancy_percent
              : null,
          outpatientsCreatedToday: safeNumber(dash?.outpatients?.created_today_count),
          paymentsDeltaAmount:
            typeof dash?.payments?.delta_amount === "number"
              ? dash.payments.delta_amount
              : null,
          paymentsDeltaDirection: dash?.payments?.delta_direction ?? null,
        });
      } catch (_) {
        if (!ignore) {
          setTodayAppointmentsCount(null);
          setActiveInpatientsCount(null);
          setOutPatientsCount(null);
          setAmountCollected(null);
          setCardMeta({
            appointmentsUpcoming: null,
            bedOccupancyPercent: null,
            outpatientsCreatedToday: null,
            paymentsDeltaAmount: null,
            paymentsDeltaDirection: null,
          });
        }
      } finally {
        if (!ignore) setLoadingStats(false);
      }
    }

    loadStats();
    return () => {
      ignore = true;
    };
  }, [facilityId, refreshKey]);

  useEffect(() => {
    let ignore = false;

    async function loadAppointments() {
      setLoadingAppts(true);
      try {
        const apptParams = new URLSearchParams({
          page: 1,
          facility: facilityId,
          today: true,
        });
        const res = await getAppointmentListApi(apptParams);
        if (ignore) return;
        setAppointmentsData(Array.isArray(res?.data?.data) ? res.data.data : []);
      } catch (e) {
        if (!ignore) setAppointmentsData([]);
      } finally {
        if (!ignore) setLoadingAppts(false);
      }
    }

    loadAppointments();
    return () => {
      ignore = true;
    };
  }, [facilityId, refreshKey]);

  const columns = useMemo(
    () => [
      {
        title: "Patient Name",
        dataIndex: "patient_first_name",
        key: "patient_first_name",
        render: (value, record) =>
          `${value ? capitalizeFirstLetter(value) : ""} ${
            record?.patient_last_name ? capitalizeFirstLetter(record.patient_last_name) : ""
          }`.trim() || "-",
      },
      {
        title: "Doctor Name",
        dataIndex: "doctor_name",
        key: "doctor_name",
        render: (value) => (value ? value : "-"),
      },
      {
        title: "Appointment time",
        dataIndex: "appointment_time",
        key: "appointment_time",
        render: (value) => formatTime(value),
      },
      {
        title: "Appointment For",
        dataIndex: "appointment_for",
        key: "appointment_for",
        render: (value) =>
          value ? (
            <Tag color={value === "consultation" ? "purple" : "blue"} style={{ borderRadius: 999, paddingInline: 12, minWidth: 90, textAlign: "center", display: "inline-block" }}>
              {value === "consultation" ? "Consultation" : "Session"}
            </Tag>
          ) : (
            "-"
          ),
      },
      {
        title: "Status",
        dataIndex: "status",
        key: "status",
        render: (value) => <StatusTag status={value} />,
      },
      {
        title: "",
        key: "arrow",
        align: "right",
        render: () => <RightOutlined className={styles.rightArrow} />,
      },
    ],
    []
  );


  const StatCard = ({
    label,
    value,
    icon,
    valuePrefix,
    metaText,
    metaTone = "muted", 
  }) => (
    <Card className={styles.statCard}>
      <div className={styles.statCardBody}>
        <div className={styles.statPrimaryRow}>
          <div className={styles.statIconWrap}>
            <img src={icon} alt="" className={styles.statIconImg} width={40} height={40} />
          </div>
          <div className={styles.statMainBlock}>
            <div className={styles.statLabel}>{label}</div>
            <div className={styles.statValue}>
            {loadingStats ? (
              <Skeleton.Input active size="small" style={{ width: 90, height: 28 }} />
            ) : value === null || value === undefined ? (
              "--"
            ) : typeof value === "number" ? (
              <>
                {valuePrefix}
                {formatCompactNumber(value)}
              </>
            ) : (
              <>
                {valuePrefix}
                {value}
              </>
            )}
            </div>
          </div>
        </div>
        {metaText ? (
          <div
            className={`${styles.statMeta} ${
              metaTone === "success"
                ? styles.metaSuccess
                : metaTone === "danger"
                  ? styles.metaDanger
                  : metaTone === "info"
                    ? styles.metaInfo
                    : ""
            }`}
          >
            {metaText}
          </div>
        ) : null}
      </div>
    </Card>
  );

  const newPatientButton = (placement) => (
    <Button
      type="primary"
      className={
        placement === "desktop"
          ? styles.newPatientBtnDesktop
          : styles.newPatientBtnMobile
      }
      onClick={() => setOpenAddPatient(true)}
    >
      <span className={styles.newPatientBtnInner}>
        <img
          src={newPatientIcon}
          alt=""
          className={styles.newPatientBtnIcon}
          width={20}
          height={20}
        />
        <span>
          {placement === "desktop" ? "New Patient" : "New Patient"}
        </span>
      </span>
    </Button>
  );

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <div className={styles.topBarText}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            Good Morning, {userDetails?.first_name || userDetails?.name || "Receptionist"}
          </Typography.Title>
          <div className={styles.subTitle}>
            Your overview of today&apos;s appointments, patients, and revenue.
          </div>
        </div>
        {newPatientButton("desktop")}
      </div>

      <div className={styles.newPatientRowMobile}>
        {newPatientButton("mobile")}
      </div>

      <div className={styles.quickActionsCol}>
        <div className={styles.quickActionsCard}>
          <div className={styles.quickActionsHeader}>
            <div className={styles.quickActionsTitle}>Quick actions</div>
          </div>
          <div className={styles.quickActionsBody}>
            <button
              type="button"
              className={styles.quickActionBtn}
              onClick={() => setOpenBookAppointment(true)}
            >
              <img
                src={actionBookAppointmentIcon}
                alt=""
                className={styles.quickActionIcon}
                width={40}
                height={40}
              />
              <span className={styles.quickActionLabel}>Book Appointment</span>
            </button>
            <button
              type="button"
              className={styles.quickActionBtn}
              onClick={() => setOpenAddInpatient(true)}
            >
              <img
                src={actionAddInpatientIcon}
                alt=""
                className={styles.quickActionIcon}
                width={40}
                height={40}
              />
              <span className={styles.quickActionLabel}>Add Inpatient</span>
            </button>
          </div>
        </div>
      </div>

      <div className={styles.statsSection}>
      <Row gutter={[16, 16]} align="stretch" className={styles.statsRow}>
        <Col xs={24} lg={6} className={styles.statCardCol}>
          <StatCard
            label="Today&apos;s Appointments"
                value={todayAppointmentsCount}
                icon={cardTodayAppointmentsIcon}
                metaText={
                  loadingStats || cardMeta.appointmentsUpcoming == null
                    ? null
                    : `${cardMeta.appointmentsUpcoming} Upcoming`
                }
            metaTone="success"
          />
        </Col>
        <Col xs={24} lg={6} className={styles.statCardCol}>
          <StatCard
            label="Inpatients Active"
                value={activeInpatientsCount}
                icon={cardInpatientsActiveIcon}
                metaText={
                  loadingStats || cardMeta.bedOccupancyPercent == null
                    ? null
                    : `${Math.round(cardMeta.bedOccupancyPercent)}% bed occupancy`
                }
            metaTone="info"
          />
        </Col>
        <Col xs={24} lg={6} className={styles.statCardCol}>
          <StatCard
            label="Outpatients"
                value={outPatientsCount}
                icon={cardOutpatientIcon}
                metaText={
                  loadingStats || cardMeta.outpatientsCreatedToday == null
                    ? null
                    : `+${cardMeta.outpatientsCreatedToday} Since morning`
                }
            metaTone="success"
          />
        </Col>
        <Col xs={24} lg={6} className={styles.statCardCol}>
          <StatCard
            label="Amount Collected"
                value={amountCollected === null ? null : formatToINR(amountCollected)}
                icon={cardAmountCollectedIcon}
                valuePrefix=""
                metaText={
                  loadingStats ||
                  cardMeta.paymentsDeltaAmount == null ||
                  cardMeta.paymentsDeltaDirection == null
                    ? null
                    : `${
                        cardMeta.paymentsDeltaDirection === "increase"
                          ? `↑ ${formatCompactNumber(cardMeta.paymentsDeltaAmount)}`
                          : cardMeta.paymentsDeltaDirection === "decrease"
                            ? `↓ ${formatCompactNumber(
                                Math.abs(cardMeta.paymentsDeltaAmount)
                              )}`
                            : `${formatCompactNumber(cardMeta.paymentsDeltaAmount)}`
                      } vs yesterday`
                }
                metaTone={
                  cardMeta.paymentsDeltaDirection === "decrease"
                    ? "danger"
                    : cardMeta.paymentsDeltaDirection === "increase"
                      ? "success"
                  : "muted"
            }
          />
        </Col>
      </Row>
      </div>

      <Card className={styles.appointmentsPanel}>
        <div className={styles.appointmentsPanelHeader}>
          <div className={styles.appointmentsPanelTitle}>Today&apos;s Appointments</div>
        </div>
        <div className={styles.appointmentsPanelBody}>
          <Table
            loading={loadingAppts}
            dataSource={appointmentsData}
            columns={columns}
            rowKey={(r) => r?.id || `${r?.appointment_date}-${r?.appointment_time}-${r?.patient_id}`}
            scroll={isMobile ? { x: "max-content" } : undefined}
            pagination={
              isMobile
                ? {
                    pageSize: 8,
                    showSizeChanger: false,
                    responsive: true,
                  }
                : false
            }
            locale={{
              emptyText: loadingAppts ? " " : <Empty description="No appointments for today" />,
            }}
          />
        </div>
      </Card>

      {openAddPatient && (
        <AddPatientModal
          open={openAddPatient}
          setOpen={setOpenAddPatient}
          refresh={() => { refresh?.(); reloadDashboard(); }}
        />
      )}

      {openBookAppointment && (
        <AppointsModal
          open={openBookAppointment}
          setOpen={setOpenBookAppointment}
          refresh={() => { refresh?.(); reloadDashboard(); }}
        />
      )}

      {openAddInpatient && (
        <InPatientModal
          open={openAddInpatient}
          setOpen={setOpenAddInpatient}
          refreshIpPage={reloadDashboard}
          refresh={() => { refresh?.(); reloadDashboard(); }}
        />
      )}
    </div>
  );
};

export default ReceptionistDashboard;

